import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [disabledBanner, setDisabledBanner] = useState(false);

  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (err) {
      if (err.response?.status === 403) {
        // Account was disabled while logged in
        setDisabledBanner(true);
        setTimeout(() => {
          localStorage.removeItem('token');
          setUser(null);
          setDisabledBanner(false);
        }, 3000);
      } else {
        localStorage.removeItem('token');
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Poll every 60s to detect if account is disabled server-side
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => { checkAuth(); }, 60000);
    return () => clearInterval(interval);
  }, [user, checkAuth]);

  const login = async (email, password) => {
    setError(null);
    const response = await api.post('/auth/login', { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setUser(userData);
    return userData;
  };

  // Step 1: send verification code - called directly from RegisterPage
  const register = async (email, password, name) => {
    setError(null);
    await api.post('/auth/register', { email, password, name });
  };

  // Step 2: verify code and create account
  const verifyEmailAndLogin = async (email, code) => {
    setError(null);
    const response = await api.post('/auth/verify-email', { email, code });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('token');
      setUser(null);
    }
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  const value = {
    user,
    loading,
    error,
    login,
    register,
    verifyEmailAndLogin,
    logout,
    updateUser,
    checkAuth,
    isAuthenticated: !!user,
    hasCompany: !!user?.company_id,
  };

  return (
    <AuthContext.Provider value={value}>
      {disabledBanner && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
          background: '#dc2626', color: '#fff', textAlign: 'center',
          padding: '14px', fontWeight: 600, fontSize: '15px'
        }}>
          Tu cuenta ha sido deshabilitada. Serás desconectado en breve. / Your account has been disabled. Logging out...
        </div>
      )}
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
